import { useState, FormEvent, ChangeEvent } from "react";
import "../css/ContactUs.css";
import { useNavigate } from "react-router-dom";
import { FaEnvelope, FaPhone } from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";
const ContactPage = () => {
    // Define state types
    const [email, setEmail] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [message, setMessage] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [successAlert, setSuccessAlert] = useState<boolean>(false);
    const navigate = useNavigate(); // Step 2: Initialize useNavigate
    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (emailError) setEmailError(""); // Clear email error immediately
    };
    const handleRecaptchaChange = (token: string | null) => {
        setCaptchaToken(token);
    };
    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
        setEmailError(""); // Reset email error in case previous error was due to submission
    };

    const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(e.target.value);
        setEmailError(""); // Reset email error in case previous error was due to submission
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (!validateEmail(email)) {
            setEmailError("Please enter a valid email address.");
            return;
        }
        if (!captchaToken) {
            setEmailError("Please complete the reCAPTCHA verification.");
            return;
        }
        setLoading(true);
        setSuccessAlert(false);

        const apiUrl = import.meta.env.VITE_BASE_URL;
        // console.log("API URL:", apiUrl); 

        if (!apiUrl) {
            setEmailError("API URL is not defined. Please check your .env file.");
            setLoading(false);
            return;
        }

        const requestBody = { name, email, message };
        try {
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(requestBody),
            });

            console.log("Request Body:", JSON.stringify(requestBody));
            console.log("Response Status:", response.status);

            const textResponse = await response.text();
            console.log("Raw Response:", textResponse);

            try {
                const data = JSON.parse(textResponse); // Handle JSON safely
                if (response.ok) {
                    setSuccessAlert(true);
                    setName("");
                    setEmail("");
                    setMessage("");
                    setCaptchaToken(null); // Reset reCAPTCHA
                    setTimeout(() => {
                        setSuccessAlert(false)
                        navigate("/"); // Redirect to home page
                    }, 3000);

                } else if (response.status === 422) {
                    if (
                        data.detail &&
                        Array.isArray(data.detail) &&
                        data.detail.length > 0
                    ) {
                        // Get the specific error
                        const error = data.detail[0];

                        // Check which field has the error and set the appropriate error state
                        if (error.loc && error.loc.includes("name")) {
                            setEmailError(
                                "Name should only contain letters, spaces, hyphens, and apostrophes."
                            );
                        } else if (error.loc && error.loc.includes("email")) {
                            setEmailError(error.msg);
                        } else if (error.loc && error.loc.includes("message")) {
                            setEmailError(
                                "Please enter a valid message with at least 10 charactors."
                            );
                        } else {
                            // If we can't determine the field, show a general error
                            setEmailError(error.msg || "Validation failed");
                        }
                    } else {
                        // Fallback for other error formats
                        setEmailError(
                            data.error || "Something went wrong, please try again."
                        );
                    }
                }
            } catch (jsonError) {
                setEmailError("Invalid JSON response from server.");
                console.error("JSON Parse Error:", jsonError);
            }

        } catch (error) {
            setEmailError("Failed to send message. Please try again later.");
            console.error("Fetch Error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="contact-container">
            {/* Success Alert */}
            {successAlert && (
                <div className="success-alert">
                    ✅  Your message has been sent successfully!
                </div>
            )}

            <div className="contact-header">
                <h1 className="contact-title">Get in Touch</h1>
                <p className="contact-description">
                    Feel free to reach out to us.
                    <br />
                    Our team is committed to responding to your inquiries promptly and efficiently.
                </p>
            </div>

            <div className="contact-content">
                <div className="contacts-info">
                    <div className="info-section">
                        <div>
                            <h2 className="section-title">Address</h2>
                            <p className="address-text">
                                <a
                                    href="https://maps.app.goo.gl/jbVtSsE7BiiuDJddA"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="map-link"
                                >
                                    #42, Samdach Pan Street(214),<br />
                                    Sangkat Boeung Raing, Khan<br />
                                    Daun Penhn, Phnom Penh,<br />
                                    120204.
                                </a>
                            </p>
                        </div>

                        <div className="contacts-info">
                            <a href="mailto:info@sayon.com.kh" className="contacts-item">
                                <FaEnvelope className="icon" />
                                <span>info@sayon.com.kh</span>
                            </a>
                            <a href="tel:+85581307999" className="contacts-item">
                                <FaPhone className="icon" />
                                <span>(855) 81 307 999</span>
                            </a>
                        </div>
                    </div>

                    <div className="info-form">
                        <h2 className="section-title">Send Us a Message</h2>
                        <form className="contact-form" onSubmit={handleSubmit}>
                            <div className="form-row">
                                <input
                                    type="text"
                                    placeholder="Name"
                                    className="form-input"
                                    value={name}
                                    onChange={handleNameChange}
                                // onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                                />
                                <input
                                    type="email"
                                    placeholder="Email"
                                    className={`form-input ${emailError ? "error-border" : ""}`}
                                    value={email}
                                    onChange={handleEmailChange}
                                />
                            </div>
                            {emailError && <p className="error-message">{emailError}</p>}
                            <textarea
                                placeholder="Message"
                                className="form-textarea"
                                value={message}
                                onChange={handleMessageChange}
                            // onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
                            ></textarea>
                            <ReCAPTCHA
                                sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                                onChange={handleRecaptchaChange}
                            />
                            <br />
                            <button type="submit" className="submit-button" disabled={loading}>
                                {loading ? "Sending..." : "Send Message"}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="map-container">
                    <iframe
                        title="Location Map"
                        className="map-frame"
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3908.772906548088!2d104.9235715717057!3d11.560728263890045!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0:0x0!2zMTHCsDMzJzM4LjYiTiAxMDTCsDU1JzI0LjkiRQ!5e0!3m2!1sen!2skh!4v1708789200000!5m2!1sen!2skh&markers=color:red%7C11.560728263890045,104.9235715717057"
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                </div>
            </div>
        </div>
    );
};

export default ContactPage;